/**
 * Session helpers: current user query, login/logout mutations, and derived `isAuthenticated`.
 *
 * Where: any protected page or layout (`ProtectedRoute`, `DashboardLayout`, feature pages).
 * Uses: `authApi`, `getAccessToken` / `clearTokens` from `api/client`, TanStack Query cache.
 * Behavior: login seeds tokens then invalidates `['user','me']`; logout clears tokens and navigates home.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/api/auth';
import { getAccessToken, clearTokens } from '@/api/client';
import type { LoginRequest } from '@/types/auth';

export const useAuth = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Check if user is authenticated
  const isAuthenticated = !!getAccessToken();

  // Get current user query
  const {
    data: user,
    isLoading: isLoadingUser
  } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: authApi.getCurrentUser,
    enabled: isAuthenticated,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (data) => {
      queryClient.setQueryData(['user', 'me'], data.user);
      navigate('/', { replace: true });
    },
    onError: (error) => {
      console.error('Login failed:', error);
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      queryClient.clear();
      clearTokens();
      navigate('/login', { replace: true });
    },
  });

  const login = (credentials: LoginRequest) => {
    loginMutation.mutate(credentials);
  };

  const logout = () => {
    logoutMutation.mutate();
  };

  return {
    user: user || null,
    isAuthenticated,
    isLoading: isLoadingUser || loginMutation.isPending || logoutMutation.isPending,
    isLoginLoading: loginMutation.isPending,
    loginError: loginMutation.error,
    login,
    logout,
  };
};
